import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { useLanguage } from '../contexts/LanguageContext'

const HowItWorks = () => {
  const { t } = useLanguage()
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })

  const steps = [
    {
      number: '1',
      title: t.howItWorks.step1.title,
      description: t.howItWorks.step1.description,
    },
    {
      number: '2',
      title: t.howItWorks.step2.title,
      description: t.howItWorks.step2.description,
    },
    {
      number: '3',
      title: t.howItWorks.step3.title,
      description: t.howItWorks.step3.description,
    },
  ]

  return (
    <section
      id="how-it-works"
      ref={ref}
      className="min-h-screen py-12 md:py-16 lg:py-24 xl:py-32 bg-gradient-to-b from-emerald-50/50 to-white relative overflow-hidden flex items-center"
    >
      <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-primary/5 to-transparent pointer-events-none" />

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8 md:mb-12 lg:mb-16 xl:mb-20"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-3 md:mb-4 lg:mb-6 text-gray-900">
            {t.howItWorks.title}
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 max-w-2xl mx-auto px-4">
            {t.howItWorks.description}
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto space-y-6 md:space-y-10 lg:space-y-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="flex flex-col md:flex-row gap-4 md:gap-6 lg:gap-8 items-start"
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                className="relative"
              >
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: index * 0.5,
                  }}
                  className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 lg:w-20 lg:h-20 gradient-bg rounded-xl md:rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold shadow-xl premium-glow relative z-10"
                >
                  {step.number}
                </motion.div>
                <motion.div
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.3,
                  }}
                  className="absolute inset-0 gradient-bg rounded-2xl blur-xl"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ delay: index * 0.2 + 0.3 }}
                className="flex-1"
              >
                <h3 className="text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 md:mb-3 lg:mb-4 text-gray-900">
                  {step.title}
                </h3>
                <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
